/**
 * Web Push on this device — the browser half of closed-app notifications.
 * `docs/notes/pwa.md` is the authority; the server half is
 * `apps/server/src/notifications/push/` and `routes/web-push.ts`.
 *
 * A subscription is `pushManager.subscribe` on the registration `sw.js` owns,
 * stored with the server as a `web_push` row of `mobile_push_installations`
 * (`PUT /me/mobile-push-installations`, endpoint as `providerToken`). The
 * worker then encrypts each fresh notification to it, and `sw.js`'s `push`
 * handler shows it whether or not a window is open.
 *
 * The rules that fail silently if broken:
 *
 *  1. **`Notification.requestPermission()` is the first thing `enable` does.**
 *     Firefox and Safari honour it only inside the user gesture, and any
 *     `await` before it spends the activation — the prompt then never shows
 *     and the call resolves `default`, which reads as a member dismissing it.
 *  2. **`getRegistration()`, never `serviceWorker.ready`.** `ready` never
 *     settles where no worker is registered (dev, e2e, any http origin), so
 *     an `await` on it hangs the toggle — and sign-out — forever.
 *  3. **A subscription made with a different VAPID key is unsubscribed
 *     first.** `subscribe` with a new key over an old subscription throws
 *     `InvalidStateError`, and a subscription to a rotated key is one the
 *     server can no longer send to.
 *  4. **Sign-out revokes before the session ends** (`useSignOut`): the revoke
 *     route needs the session, and a shared phone must stop receiving the
 *     previous member's pushes. The local unsubscribe happens whatever the
 *     revoke does, so a failed request still kills the endpoint — the next
 *     send gets `410` and the server revokes the row itself.
 *  5. **The load-time refresh re-registers only a subscription the server
 *     still knows this member by.** It re-`PUT`s when the caller has at least
 *     one live `web_push` installation, so a subscription left behind by a
 *     previous member whose sign-out could not reach the server is not
 *     silently adopted by the next one. (The installation list carries no
 *     endpoint, so "a live web row" is the closest the client can name.)
 *
 * Every function returns without a network call when the browser has no
 * worker registration or no subscription — jsdom has neither, which is what
 * keeps the unit suites' fetch counts unchanged.
 */
import {
  mobilePushInstallationListSchema,
  mobilePushInstallationPutSchema,
  webPushConfigSchema,
  webPushSubscriptionRevokeSchema,
  type MobilePushInstallationList,
  type WebPushConfig,
} from '@molt/shared';
import { get, post, put } from './api';
import { queryKeys } from './query-keys';

export interface WebPushEnvironment {
  readonly notification:
    Pick<typeof Notification, 'permission' | 'requestPermission'> | undefined;
  readonly serviceWorker: Pick<ServiceWorkerContainer, 'getRegistration'> | undefined;
  readonly pushManager: boolean;
}

export function browserWebPushEnvironment(): WebPushEnvironment {
  if (typeof window === 'undefined') {
    return { notification: undefined, serviceWorker: undefined, pushManager: false };
  }
  return {
    notification: 'Notification' in window ? window.Notification : undefined,
    serviceWorker: 'serviceWorker' in navigator ? navigator.serviceWorker : undefined,
    pushManager: 'PushManager' in window,
  };
}

/** Can this browser hold a push subscription at all? */
export function webPushSupported(env: WebPushEnvironment = browserWebPushEnvironment()) {
  return (
    env.notification !== undefined && env.serviceWorker !== undefined && env.pushManager
  );
}

export type WebPushPermission = NotificationPermission | 'unsupported';

export function webPushPermission(
  env: WebPushEnvironment = browserWebPushEnvironment(),
): WebPushPermission {
  return webPushSupported(env) && env.notification !== undefined
    ? env.notification.permission
    : 'unsupported';
}

/** `sw.js`'s registration, or null — rule 2: never `serviceWorker.ready`. */
export async function webPushRegistration(
  env: WebPushEnvironment = browserWebPushEnvironment(),
): Promise<ServiceWorkerRegistration | null> {
  if (!webPushSupported(env) || env.serviceWorker === undefined) return null;
  try {
    return (await env.serviceWorker.getRegistration('/')) ?? null;
  } catch {
    return null;
  }
}

/** This device's subscription, or null when there is none to speak of. */
export async function currentWebPushSubscription(
  env: WebPushEnvironment = browserWebPushEnvironment(),
): Promise<PushSubscription | null> {
  const registration = await webPushRegistration(env);
  if (registration === null) return null;
  return registration.pushManager.getSubscription();
}

export function getWebPushConfig(): Promise<WebPushConfig> {
  return get('/me/web-push-config', webPushConfigSchema);
}

export function webPushConfigQueryOptions() {
  return {
    queryKey: queryKeys.notifications.webPushConfig(),
    queryFn: getWebPushConfig,
    staleTime: Infinity,
  };
}

/** A VAPID public key (base64url, unpadded) as the bytes `subscribe` takes. */
export function applicationServerKeyBytes(key: string): Uint8Array<ArrayBuffer> {
  const base64 = key.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function sameKey(existing: ArrayBuffer | null, key: Uint8Array): boolean {
  if (existing === null) return false;
  const bytes = new Uint8Array(existing);
  return bytes.length === key.length && bytes.every((byte, i) => byte === key[i]);
}

function putSubscription(
  subscription: PushSubscription,
  init?: { signal?: AbortSignal },
): Promise<MobilePushInstallationList> {
  const json = subscription.toJSON();
  const body = mobilePushInstallationPutSchema.parse({
    platform: 'web',
    provider: 'web_push',
    providerToken: subscription.endpoint,
    permissionStatus: 'granted',
    keys: { p256dh: json.keys?.['p256dh'], auth: json.keys?.['auth'] },
  });
  return put(
    '/me/mobile-push-installations',
    mobilePushInstallationListSchema,
    body,
    init,
  );
}

/**
 * Subscribe with `publicKey`, replacing a subscription to any other key
 * (rule 3), and store it with the server. A subscription THIS call created is
 * dropped again when the store fails, so a failed enable never leaves a live
 * endpoint the server does not know — the toggle reads the local
 * subscription, and the load-time refresh (rule 5) would never repair it.
 */
async function subscribeAndStore(
  registration: ServiceWorkerRegistration,
  publicKey: string,
): Promise<void> {
  const key = applicationServerKeyBytes(publicKey);
  const existing = await registration.pushManager.getSubscription();
  if (existing !== null && sameKey(existing.options.applicationServerKey, key)) {
    await putSubscription(existing);
    return;
  }
  if (existing !== null) await existing.unsubscribe();
  const created = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key,
  });
  try {
    await putSubscription(created);
  } catch (error) {
    await created.unsubscribe().catch(() => false);
    throw error;
  }
}

export type EnableWebPushResult =
  'enabled' | 'denied' | 'dismissed' | 'unsupported' | 'unconfigured' | 'unregistered';

/**
 * Turn push on for this device. Call it from the click handler itself —
 * rule 1. Network and storage failures reject; the outcomes a member caused
 * (a denied or dismissed prompt) and the ones this deployment caused (no
 * VAPID key, no worker) resolve, so the caller can say which it was.
 */
export async function enableWebPush(
  env: WebPushEnvironment = browserWebPushEnvironment(),
): Promise<EnableWebPushResult> {
  if (!webPushSupported(env) || env.notification === undefined) return 'unsupported';
  const current = env.notification.permission;
  // Rule 1: nothing is awaited before this line.
  const permission =
    current === 'default' ? await env.notification.requestPermission() : current;
  if (permission === 'denied') return 'denied';
  if (permission !== 'granted') return 'dismissed';

  const { publicKey } = await getWebPushConfig();
  if (publicKey === null) return 'unconfigured';
  const registration = await webPushRegistration(env);
  if (registration === null) return 'unregistered';

  await subscribeAndStore(registration, publicKey);
  return 'enabled';
}

/**
 * Turn push off for this device: revoke the server row (while the session
 * lasts), then unsubscribe locally whatever the revoke did — rule 4. Resolves
 * false when there was no subscription; rejects with the revoke's failure
 * after the local unsubscribe has still happened.
 */
export async function disableWebPush(
  init: { signal?: AbortSignal } = {},
  env: WebPushEnvironment = browserWebPushEnvironment(),
): Promise<boolean> {
  const subscription = await currentWebPushSubscription(env);
  if (subscription === null) return false;
  try {
    await post(
      '/me/web-push-subscription/revoke',
      mobilePushInstallationListSchema,
      webPushSubscriptionRevokeSchema.parse({ endpoint: subscription.endpoint }),
      init,
    );
  } finally {
    try {
      await subscription.unsubscribe();
    } catch {
      // An endpoint the browser could not drop still gets a 410 from its push
      // service once the server stops honouring it; nothing to do here.
    }
  }
  return true;
}

/**
 * The sign-out path: `disableWebPush`, bounded and silent. Sign-out must
 * proceed whatever the push service, the worker or the network does.
 */
export async function disableWebPushBeforeSignOut(
  timeoutMs = 3000,
  env: WebPushEnvironment = browserWebPushEnvironment(),
): Promise<void> {
  const signal = AbortSignal.timeout(timeoutMs);
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      disableWebPush({ signal }, env),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, timeoutMs);
      }),
    ]);
  } catch {
    // Best effort, by design.
  } finally {
    clearTimeout(timer);
  }
}

export type RefreshWebPushResult = 'refreshed' | 'skipped';

/**
 * On load, with a member signed in: re-`PUT` this device's subscription so
 * `last_seen_at` and the keys stay current and a VAPID key rotation is picked
 * up. Only when permission is granted, a subscription exists, and the server
 * still lists a live `web_push` installation for this member — rule 5.
 */
export async function refreshWebPushOnLoad(
  env: WebPushEnvironment = browserWebPushEnvironment(),
): Promise<RefreshWebPushResult> {
  if (webPushPermission(env) !== 'granted') return 'skipped';
  const registration = await webPushRegistration(env);
  if (registration === null) return 'skipped';
  const existing = await registration.pushManager.getSubscription();
  if (existing === null) return 'skipped';

  const installations = await get(
    '/me/mobile-push-installations',
    mobilePushInstallationListSchema,
  );
  const known = installations.installations.some(
    (installation) =>
      installation.provider === 'web_push' && installation.permissionStatus === 'granted',
  );
  if (!known) return 'skipped';

  const { publicKey } = await getWebPushConfig();
  if (publicKey === null) return 'skipped';
  await subscribeAndStore(registration, publicKey);
  return 'refreshed';
}
